import React, { useEffect, useState } from 'react';
import CollapsibleCard from '@core/components/CollapsibleCard';
import { fetchScopeReportMembership } from '../api/membershipApi';
import type { MembershipTallies, ScopeReportMembership } from '../types';
import { dayBeforeIso, formatCount, formatShortDate } from '../utils/membershipFormat';
import MembershipBreakdownTable from './MembershipBreakdownTable';
import '../membership.css';

interface Props {
  scope: 'district' | 'nation';
  scopeId: number;
  departmentId: number;
  reportTagId: number;
  /** Changing this value reloads the figures, for example after the report status changes. */
  reloadToken?: string;
}

const TALLY_TILES: Array<{ key: keyof MembershipTallies; label: string; sign: '+' | '-' }> = [
  { key: 'added', label: 'Added', sign: '+' },
  { key: 'transferred_in', label: 'Transferred in', sign: '+' },
  { key: 'deceased', label: 'Deceased', sign: '-' },
  { key: 'transferred_out', label: 'Transferred out', sign: '-' },
  { key: 'excluded', label: 'Excluded', sign: '-' },
];

function shareOf(part?: number | null, total?: number | null): number {
  if (!total || !part) return 0;
  return Math.round((part / total) * 100);
}

function changesLabel(since: string, until: string): string {
  if (since.slice(0, 7) === until.slice(0, 7)) {
    const month = new Date(`${since.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    return `${month} changes`;
  }
  return 'Changes in this period';
}

/** Read-only membership section on a district or national report, rolled up from the churches. */
const RollupReportMembershipSection: React.FC<Props> = ({ scope, scopeId, departmentId, reportTagId, reloadToken }) => {
  const reportPath = `${scope}/${scopeId}/department/${departmentId}/report/reporttag/${reportTagId}`;
  const [membership, setMembership] = useState<ScopeReportMembership | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState(true);

  useEffect(() => {
    setMembership(null);
  }, [reportPath]);

  useEffect(() => {
    let cancelled = false;
    fetchScopeReportMembership(reportPath).then((response) => {
      if (cancelled) return;
      setMembership(response?.isSuccess() ? response.getData() ?? null : null);
    });
    return () => {
      cancelled = true;
    };
  }, [reportPath, reloadToken]);

  if (!membership?.enabled || !membership.since || !membership.until) return null;

  const { since, until } = membership;
  const sinceLabel = formatShortDate(since);
  const untilLabel = formatShortDate(until);
  const malePct = shareOf(membership.male_count, membership.total_members);
  const femalePct = shareOf(membership.female_count, membership.total_members);
  const tallies = membership.tallies;
  const kind = scope === 'district' ? 'District' : 'National';
  const breakdownId = `report-membership-breakdown-${scope}`;

  return (
    <CollapsibleCard
      className="ntcog-card dashboard-section-card membership-card membership-card--modern membership-card--report mb-4"
      title="Membership"
      subtitle={`${kind} membership for ${sinceLabel} \u2013 ${untilLabel}`}
      subtitleClassName="membership-subtitle text-muted small mb-0"
      bodyId="report-membership-body"
    >
      <div className="membership-stats membership-stats--3">
        <div className="membership-stat">
          <div className="membership-stat__label">Total members as at {untilLabel}</div>
          <div className="membership-stat__value">{formatCount(membership.total_members)}</div>
          {membership.opening_total != null && (
            <div className="membership-stat__note">
              {formatCount(membership.opening_total)} at {formatShortDate(dayBeforeIso(since))}
            </div>
          )}
        </div>
        <div className="membership-stat">
          <div className="membership-stat__label">Male</div>
          <div className="membership-stat__value">{formatCount(membership.male_count)}</div>
          <div className="membership-stat__bar" aria-hidden="true"><span style={{ width: `${malePct}%` }} /></div>
          <div className="membership-stat__note">{malePct}% of members</div>
        </div>
        <div className="membership-stat">
          <div className="membership-stat__label">Female</div>
          <div className="membership-stat__value">{formatCount(membership.female_count)}</div>
          <div className="membership-stat__bar" aria-hidden="true"><span style={{ width: `${femalePct}%` }} /></div>
          <div className="membership-stat__note">{femalePct}% of members</div>
        </div>
      </div>

      <h3 className="membership-section-label">{changesLabel(since, until)}</h3>
      <p className="membership-hint mb-2">Changes recorded in this period. Zeros mean nothing was recorded.</p>
      <div className="membership-records">
        {TALLY_TILES.map((tile) => (
          <div className="membership-record" key={tile.key}>
            <div className="membership-record__label">
              <span
                className={`membership-record__sign membership-record__sign--${tile.sign === '+' ? 'in' : 'out'}`}
                aria-hidden="true"
              >
                {tile.sign === '+' ? '+' : '\u2212'}
              </span>
              {tile.label}
            </div>
            <div className="membership-record__value">{formatCount(tallies?.[tile.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      <div className="membership-history-block">
        <h3 className="h6 mb-2">
          <button
            type="button"
            className="membership-caret btn btn-link btn-sm"
            aria-expanded={breakdownOpen}
            aria-controls={breakdownId}
            onClick={() => setBreakdownOpen((value) => !value)}
          >
            <span className="membership-caret__mark" aria-hidden="true">{breakdownOpen ? '\u25BE' : '\u25B8'}</span>
            Membership breakdown
          </button>
        </h3>
        {breakdownOpen && (
          <div id={breakdownId}>
            <MembershipBreakdownTable
              scope={scope === 'district' ? 'DISTRICT' : 'NATION'}
              churches={membership.churches}
              districts={membership.districts}
            />
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
};

export default RollupReportMembershipSection;
